import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { Blocker } from '../../../../../core/models';
import { TemplateType } from '../../../../../core/enums';
import { BlockerService } from '../../../../../core/services/blocker.service';
import { GeneralActionDialogComponent } from '../../crud/general-action-dialog/general-action-dialog.component';
import { LayoutUtilsService, MessageType } from '../../../../../core/_base/crud';

interface FormValue {
  code: string;
  description: string;
  triggerFollowUp: boolean;
  isActive: boolean;
}

@Component({
  selector: 'kt-add-blocker',
  templateUrl: './add-blocker.component.html',
})
export class AddBlockerComponent implements OnInit {
  TemplateType = TemplateType;
  form: FormGroup;
  blockers: Partial<Blocker>[];
  index = '';
  @Input() templateType: TemplateType;
  @Output() addedBlockers = new EventEmitter<Partial<Blocker>[]>();
  @Input() mode;
  private _templateId: number;
  @Input() set templateId(value: number) {
    if (!value) {
      this.blockers = [];

      return;
    }
    this._templateId = value;
    this.loadBlockers(value);
  }

  get templateId(): number {
    return this._templateId;
  }

  private _emitBlockers: boolean;
  @Input() set emitBlockers(value: boolean) {
    if (value) this.addedBlockers.emit(this.blockers);
  }

  get emitBlockers(): boolean {
    return this._emitBlockers;
  }

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private blockerService: BlockerService,
    private cdr: ChangeDetectorRef,
    private layoutUtilsService: LayoutUtilsService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      code: ['', Validators.required],
      description: ['', Validators.required],
      triggerFollowUp: [false, Validators.required],
      isActive: [true, Validators.required],
    });
    // View Mode
    if (this.mode === 'view') this.form.disable();
  }

  reset(): void {
    this.index = '';

    this.form.reset({
      code: '',
      description: '',
      triggerFollowUp: false,
      isActive: true,
    });
  }

  hasError(controlName: string, errorType: 'required' | 'duplicate'): boolean {
    const control = this.form.get(controlName);

    return control.touched && control.hasError(errorType);
  }

  getErrorMessage(controlLabel: string, errorType: 'required' | 'duplicate'): string {
    switch (errorType) {
      case 'required':
        return `${controlLabel} field is required`;
      case 'duplicate':
        return `${controlLabel} field must be unique`;
    }
  }

  mapFormToBlocker(value: FormValue): Partial<Blocker> {
    return {
      code: value.code,
      description: value.description,
      is_active: value.isActive,
      trigger_follow_up: value.triggerFollowUp,
    };
  }

  mapBlockerToForm(blocker: Partial<Blocker>): FormValue {
    return {
      code: blocker.code,
      description: blocker.description,
      isActive: blocker.is_active,
      triggerFollowUp: blocker.trigger_follow_up,
    };
  }

  addBlocker(event: Event): void {
    event.stopPropagation();

    const formValue = this.form.value;
    let blockers = [...this.blockers];

    if (this.index) {
      blockers = blockers.map((blocker, index) =>
        index === +this.index ? { ...blocker, ...this.mapFormToBlocker(formValue) } : blocker,
      );
    } else {
      blockers.unshift(this.mapFormToBlocker(formValue));
    }

    if (
      blockers.filter((blocker) => blocker.code.trim().localeCompare(formValue.code.trim()) === 0)
        .length > 1
    ) {
      this.form.get('code')?.setErrors({ duplicate: true });
      this.dialog.open(GeneralActionDialogComponent, {
        width: '400px',
        disableClose: false,
        closeOnNavigation: true,
        data: {
          description: `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">Unable to add new code. Code field must be unique.</p>
            </div>
          `,
        },
      });
    }

    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    this.blockers = blockers;
    this.updateFinalBlockers();
    this.reset();
  }

  updateBlocker(event: Event, idx: number): void {
    event.stopPropagation();

    const blocker = this.blockers.find((record, index) => index === idx);

    this.index = `${idx}`;
    this.form.patchValue(this.mapBlockerToForm(blocker));
  }

  cancelBlockerUpdate(event: Event): void {
    event.stopPropagation();

    this.reset();
  }

  deleteBlocker(event: Event, idx: number): void {
    event.stopPropagation();
    const _title = 'Delete Additional Step';
    const _description = 'Are you sure you want to delete this blocker?';
    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, 'Loading...');
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      idx === 0 ? this.blockers.shift() : this.blockers.splice(idx, 1);
      this.updateFinalBlockers();
    });
  }

  loadBlockers(templateID) {
    this.blockerService
      .getBlockers(templateID, this.templateType)
      .pipe(take(1))
      .subscribe((blockers) => {
        this.blockers = blockers || [];
        this.cdr.detectChanges();
      });
  }
  updateFinalBlockers() {
    this.blockerService
      .postPutBlockersTemplate({ blockers: this.blockers }, this._templateId)
      .subscribe(() => {
        this.loadBlockers(this._templateId);
        this.layoutUtilsService.showActionNotification(
          'Blockers updated successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
      });
  }
}
